import type { ComponentType, ReactNode } from 'react';
import { useElementWidth } from '../../../hooks/useElementWidth';
import { enabledFirst } from './taskActionOrder';

export interface TaskActionItem {
    key: string;
    icon: ComponentType<{ className?: string }>;
    label: string;
    onSelect: () => void;
    disabled?: boolean;
    disabledReason?: string;
}

// Below this list width the longest labels no longer fit at text-xs, so the buttons tighten up.
const DENSE_BELOW_PX = 300;

const BUTTON_BASE_CLASS =
    'flex h-6 min-w-0 items-center rounded-lg border border-gray-200 bg-white text-left font-medium text-gray-700 transition-colors';
const ROOMY_CLASS = 'gap-1.5 px-2 text-xs';
const DENSE_CLASS = 'gap-1 px-1.5 text-[11px]';
const ENABLED_CLASS = 'hover:border-yellow-300 hover:bg-yellow-50';
const DISABLED_CLASS = 'opacity-40 cursor-not-allowed';

interface TaskActionButtonProps extends Omit<TaskActionItem, 'key'> {
    dense: boolean;
}

function TaskActionButton({
    icon: Icon,
    label,
    onSelect,
    disabled,
    disabledReason,
    dense
}: TaskActionButtonProps) {
    return (
        <button
            type="button"
            onClick={onSelect}
            disabled={disabled}
            title={disabled ? (disabledReason ?? label) : label}
            className={`${BUTTON_BASE_CLASS} ${dense ? DENSE_CLASS : ROOMY_CLASS} ${disabled ? DISABLED_CLASS : ENABLED_CLASS}`}
        >
            <Icon
                className={`${dense ? 'h-3 w-3' : 'h-3.5 w-3.5'} flex-shrink-0 ${disabled ? 'text-gray-400' : 'text-yellow-600'}`}
            />
            <span className="truncate">{label}</span>
        </button>
    );
}

interface TaskActionsBoxProps {
    items: readonly TaskActionItem[];
    /** Right side of the header, e.g. the manufacturer and DM links. */
    headerRight?: ReactNode;
}

// The task's ways out as a compact two-column list, what can be done now first.
export function TaskActionsBox({ items, headerRight }: TaskActionsBoxProps) {
    const { ref, width } = useElementWidth<HTMLDivElement>();
    const dense = width > 0 && width < DENSE_BELOW_PX;
    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex-shrink-0">
            <div className="flex items-center justify-between gap-3 px-4 py-1 border-b border-gray-100">
                <span className="font-bold text-sm text-gray-800">Actions</span>
                {headerRight}
            </div>
            <div ref={ref} className="grid grid-cols-2 gap-1 p-2">
                {enabledFirst(items).map(({ key, ...action }) => (
                    <TaskActionButton key={key} {...action} dense={dense} />
                ))}
            </div>
        </div>
    );
}
